import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaWhatsapp,
  FaPlus,
  FaUsers,
  FaCalendarAlt,
  FaEdit,
  FaSave,
  FaTimes,
  FaEllipsisV,
} from "react-icons/fa";
import * as XLSX from "xlsx";

import {
  updateCampaign,
  deleteCampaign,
  getCampaigns,
} from "../../../services/communication.services";
import CreateCampaignModal from "./campaigns/CreateCampaignModal";
import EditDelete from "./campaigns/EditDelete";
import { formatDateTime } from "../../../utils/dateFormat";

const Campaigns = () => {
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("email");
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await getCampaigns();
      setCampaigns(response);
    } catch (err) {
      setError(err.message || "Failed to fetch campaigns.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Filter campaigns based on the active tab (Email or Whatsapp)
  const filteredCampaigns = campaigns
    .filter((campaign) => campaign.channel.toLowerCase() === activeTab)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Handle campaign update
  const handleCampaignUpdate = (updatedCampaign) => {
    setCampaigns(
      campaigns.map((campaign) =>
        campaign.id === updatedCampaign.id ? updatedCampaign : campaign
      )
    );
  };

  // Handle campaign deletion
  const handleCampaignDelete = (campaignId) => {
    setCampaigns(campaigns.filter((campaign) => campaign.id !== campaignId));
  };

  // Mobile Campaign Card Component
  const CampaignCard = ({ campaign }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 mr-3">
          {/* <EditableName
            fetchCampaigns={fetchCampaigns}
            campaignId={campaign.id}
            name={campaign.name}
            onNameUpdate={handleCampaignUpdate}
            isEditing={editingId === campaign.id}
            setEditingId={setEditingId}
            isMobile={isMobile}
          /> */}
          <h3 className="text-lg font-semibold text-gray-900">
            {campaign.name}
          </h3>

          <div className="flex items-center mt-2 space-x-3">
            <div className="flex items-center">
              <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
              <span className="text-xs text-green-600 font-medium">
                {campaign.status}
              </span>
            </div>
            <span className="text-xs text-gray-500">#{campaign.id}</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <span
            className={`p-2 rounded-lg ${
              activeTab === "email"
                ? "bg-blue-100 text-blue-600"
                : "bg-green-100 text-green-600"
            }`}
          >
            {activeTab === "email" ? <FaEnvelope /> : <FaWhatsapp />}
          </span>
          <EditDelete
            campaignId={campaign.id}
            campaignData={campaign}
            onCampaignUpdate={handleCampaignUpdate}
            onCampaignDelete={handleCampaignDelete}
            setEditingId={setEditingId}
          />
        </div>
      </div>

      <div className="flex items-center text-xs text-gray-500 mb-3">
        <FaCalendarAlt className="mr-1" />
        Sent on {formatDateTime(campaign.created_at)}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center mb-1">
            <FaUsers className="text-gray-400 mr-1" />
            <span className="text-xs text-gray-500">Total Contacts</span>
          </div>
          <div className="text-lg font-bold text-gray-900">
            {campaign.total_contacts || 0}
          </div>
        </div>

        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center mb-1">
            <span className="text-xs text-gray-500">
              {activeTab === "email" ? "Emails Sent" : "Whatsapp Sent"}
            </span>
          </div>
          <div className="text-lg font-bold text-gray-900">
            {activeTab === "email"
              ? campaign.emails_sent
              : campaign.whatsapp_delivered || 0}
          </div>
        </div>
      </div>
    </div>
  );

  // Desktop Campaign Row Component
  const CampaignRow = ({ campaign }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex-1 mb-4 lg:mb-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {/* <EditableName
                fetchCampaigns={fetchCampaigns}
                campaignId={campaign.id}
                name={campaign.name}
                onNameUpdate={handleCampaignUpdate}
                isEditing={editingId === campaign.id}
                setEditingId={setEditingId}
                isMobile={false}
              /> */}
              <h3 className="text-lg font-semibold text-gray-900">
                {campaign.name}
              </h3>

              <div className="flex items-center mt-2 space-x-4">
                <div className="flex items-center">
                  <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
                  <span className="text-sm text-green-600 font-medium">
                    {campaign.status}
                  </span>
                </div>
                <span className="text-sm text-gray-500">#{campaign.id}</span>
                <div className="flex items-center text-sm text-gray-500">
                  <FaCalendarAlt className="mr-1" />
                  Sent on {formatDateTime(campaign.created_at)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between lg:justify-end space-x-6">
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <FaUsers className="text-gray-400 mr-1 text-sm" />
                <span className="text-xs text-gray-500">Total Contacts</span>
              </div>
              <div className="text-lg font-bold text-gray-900">
                {campaign.total_contacts || 0}
              </div>
            </div>

            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">
                {activeTab === "email" ? "Emails Sent" : "Whatsapp Sent"}
              </div>
              <div className="text-lg font-bold text-gray-900">
                {activeTab === "email"
                  ? campaign.emails_sent
                  : campaign.whatsapp_delivered || 0}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span
              className={`p-2 rounded-lg ${
                activeTab === "email"
                  ? "bg-blue-100 text-blue-600"
                  : "bg-green-100 text-green-600"
              }`}
            >
              {activeTab === "email" ? <FaEnvelope /> : <FaWhatsapp />}
            </span>
            <EditDelete
              campaignId={campaign.id}
              campaignData={campaign}
              onCampaignUpdate={handleCampaignUpdate}
              onCampaignDelete={handleCampaignDelete}
              setEditingId={setEditingId}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`min-h-screen bg-gray-50 transition-all duration-300 ${
        isSideBarOpen ? "ml-0 lg:ml-80" : "ml-0 md:ml-20"
      }`}
    >
      <div className="p-4 sm:p-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800">
              Campaigns
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage your email and WhatsApp campaigns
            </p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full sm:w-auto bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
          >
            <FaPlus className="mr-2" />
            Create Campaign
          </button>
          {isModalOpen && (
            <CreateCampaignModal
              fetchCampaigns={fetchCampaigns}
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </div>

        {/* Tabs Section */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-200 rounded-lg p-1">
            <button
              className={`flex-1 sm:flex-none sm:px-6 py-3 rounded-md transition-colors flex items-center justify-center ${
                activeTab === "email"
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("email")}
            >
              <FaEnvelope className="mr-2" />
              Email
            </button>
            <button
              className={`flex-1 sm:flex-none sm:px-6 py-3 rounded-md transition-colors flex items-center justify-center ${
                activeTab === "whatsapp"
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("whatsapp")}
            >
              <FaWhatsapp className="mr-2" />
              WhatsApp
            </button>
          </div>
        </div>

        {/* Campaigns List Section */}
        <div>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">Loading campaigns...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <div
                className={`mx-auto h-16 w-16 mb-4 rounded-full flex items-center justify-center ${
                  activeTab === "email"
                    ? "bg-blue-100 text-blue-600"
                    : "bg-green-100 text-green-600"
                }`}
              >
                {activeTab === "email" ? (
                  <FaEnvelope className="text-2xl" />
                ) : (
                  <FaWhatsapp className="text-2xl" />
                )}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {activeTab} campaigns yet
              </h3>
              <p className="text-gray-600 mb-4">
                Create your first {activeTab} campaign to get started
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FaPlus className="mr-2 inline" />
                Create First Campaign
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-medium text-gray-900">
                  {filteredCampaigns.length} {activeTab} campaign
                  {filteredCampaigns.length !== 1 ? "s" : ""}
                </h2>
                <span className="text-sm text-gray-500">
                  Sorted by most recent
                </span>
              </div>

              {/* Mobile View - Cards */}
              <div className="block md:hidden">
                {filteredCampaigns.map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} />
                ))}
              </div>

              {/* Desktop View - Enhanced Rows */}
              <div className="hidden md:block">
                {filteredCampaigns.map((campaign) => (
                  <CampaignRow key={campaign.id} campaign={campaign} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Editable Name Component
const EditableName = ({
  fetchCampaigns,
  campaignId,
  name,
  onNameUpdate,
  isEditing,
  setEditingId,
  isMobile,
}) => {
  const [editedName, setEditedName] = useState(name);

  const handleEdit = async () => {
    if (!editedName.trim()) {
      alert("Campaign name cannot be empty");
      return;
    }

    try {
      const updatedData = { name: editedName };
      const updatedCampaign = await updateCampaign(campaignId, updatedData);
      fetchCampaigns();
      setEditingId(null);
    } catch (error) {
      console.error("Error updating campaign:", error.message);
      alert("Failed to update campaign name");
    }
  };

  const handleCancel = () => {
    setEditedName(name);
    setEditingId(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleEdit();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return isEditing ? (
    <div className={`flex items-center space-x-2 ${isMobile ? "mb-2" : ""}`}>
      <input
        type="text"
        value={editedName}
        onChange={(e) => setEditedName(e.target.value)}
        onKeyPress={handleKeyPress}
        className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-w-0 flex-1"
        placeholder="Enter campaign name"
        autoFocus
      />
      <button
        onClick={handleEdit}
        className="bg-green-100 text-green-700 p-1.5 rounded-md hover:bg-green-200 transition-colors"
        title="Save"
      >
        <FaSave />
      </button>
      <button
        onClick={handleCancel}
        className="bg-gray-100 text-gray-700 p-1.5 rounded-md hover:bg-gray-200 transition-colors"
        title="Cancel"
      >
        <FaTimes />
      </button>
    </div>
  ) : (
    <div className="group">
      <h3
        onClick={() => setEditingId(campaignId)}
        className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-purple-600 transition-colors flex items-center"
        title="Click to edit"
      >
        {name}
        <FaEdit className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-sm text-gray-400" />
      </h3>
    </div>
  );
};

export default Campaigns;
